import { Helmet } from 'react-helmet-async';
import { useI18n } from '../../contexts/I18nContext';

interface SEOProps {
  title: string;
  description: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  article?: boolean;
  published_time?: string;
  modified_time?: string;
  tags?: string[];
  noIndex?: boolean;
  jsonLd?: Record<string, any>;
  includeAdsense?: boolean;
}

export function SEO({ 
  title, 
  description, 
  og_title, 
  og_description, 
  og_image,
  article,
  published_time,
  modified_time,
  tags,
  noIndex,
  jsonLd,
  includeAdsense
}: SEOProps) {
  const { currentLang, getAbsoluteUrlForLang } = useI18n();

  // Récupérer l'URL de la page actuelle sans la langue
  const currentPath = window.location.pathname.replace(/^\/(en\/)?/, '/');
  
  // Construire les URLs absolues pour chaque langue
  const frUrl = getAbsoluteUrlForLang('fr', currentPath);
  const enUrl = getAbsoluteUrlForLang('en', currentPath);
  
  // L'URL canonique est celle de la langue actuelle
  const canonicalUrl = currentLang === 'en' ? enUrl : frUrl;

  return (
    <Helmet>
      {/* Définir la langue du document HTML */}
      <html lang={currentLang} />
      
      <title>{title}</title>
      <meta name="description" content={description} key="description" />
      
      {/* Open Graph */}
      <meta property="og:title" content={og_title || title} key="og:title" />
      <meta property="og:description" content={og_description || description} key="og:description" />
      {og_image && <meta property="og:image" content={og_image} key="og:image" />}
      <meta property="og:url" content={canonicalUrl} key="og:url" />
      
      {/* Structure d'article si applicable */}
      {article && (
        <>
          <meta property="og:type" content="article" />
          {published_time && <meta property="article:published_time" content={published_time} />}
          {modified_time && <meta property="article:modified_time" content={modified_time} />}
          {tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={og_title || title} />
      <meta name="twitter:description" content={og_description || description} />
      {og_image && <meta name="twitter:image" content={og_image} />}
      
      {/* URL canonique */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Balises hreflang pour l'internationalisation */}
      <link rel="alternate" href={frUrl} hrefLang="fr" />
      <link rel="alternate" href={enUrl} hrefLang="en" />
      <link rel="alternate" href={frUrl} hrefLang="x-default" />
      
      {/* No index si spécifié */}
      {noIndex && <meta name="robots" content="noindex" />}
      
      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      
      {/* AdSense script */}
      {includeAdsense && (
        <script 
          async 
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4579146101917687" 
          crossOrigin="anonymous"
        />
      )}
    </Helmet>
  );
}
